export interface BlogContentBlock {
    type: 'paragraph' | 'heading' | 'list';
    text?: string;
    items?: string[];
}

export interface BlogPost {
    slug: string;
    title: string;
    image: string;
    date: string;
    author: string;
    category: 'Travel Guide' | 'Adventure' | 'Monsoon Special' | 'Food Guide';
    readTime: string;
    summary: string;
    content: BlogContentBlock[];
}

export default function getBlogs(): BlogPost[] {
    return [
        {
            slug: "mumbai-to-ratnagiri-road-trip",
            title: "The Ultimate Mumbai to Ratnagiri Road Trip Guide",
            image: "mumbai-to-ratnagiri.webp",
            date: "July 25, 2026",
            author: "Amit Shinde",
            category: "Travel Guide",
            readTime: "6 min read",
            summary: "Plan your scenic journey from Mumbai to the heart of Konkan. Explore routes, highway updates, must-visit stops, and food spots along the way.",
            content: [
                {
                    type: "paragraph",
                    text: "Ratnagiri, a historical coastal city in Maharashtra, is a treasure trove of scenic beaches, historical forts, and the world-famous Alphonso mangoes. A road trip from Mumbai to Ratnagiri is one of the most rewarding journeys you can undertake, taking you through winding ghats, lush green valleys, and pristine coastal landscapes."
                },
                {
                    type: "heading",
                    text: "Choosing the Right Route: NH 66 vs. Expressway"
                },
                {
                    type: "paragraph",
                    text: "There are two primary routes to reach Ratnagiri from Mumbai:"
                },
                {
                    type: "list",
                    items: [
                        "Route 1: Mumbai - Pune - Karad - Kokrud - Amba Ghat - Ratnagiri (Approx. 440 km, 8-9 hours). This is currently the most preferred route. The Mumbai-Pune Expressway and Pune-Bangalore Highway offer smooth 4-lane roads, and Amba Ghat provides breathtaking views of the Sahyadri range.",
                        "Route 2: Mumbai - Mangaon - Chiplun - Sangameshwar - Ratnagiri via NH 66 (Approx. 340 km, 7-8 hours). While shorter in distance, the NH 66 is undergoing widening. It offers typical rustic Konkan scenery but requires careful driving through ghat sections."
                    ]
                },
                {
                    type: "heading",
                    text: "Top Pit-stops & Sightseeing Along the Way"
                },
                {
                    type: "paragraph",
                    text: "Break your journey with these highly recommended stopovers:"
                },
                {
                    type: "list",
                    items: [
                        "Amba Ghat: A mountain pass offering gorgeous valley views. Perfect for morning tea and photography.",
                        "Marleshwar Temple & Waterfall: A cave temple dedicated to Lord Shiva surrounded by a perennial waterfall, located just a short detour from Sangameshwar.",
                        "Ganpatipule Beach: A serene beach famous for its 400-year-old self-manifested Ganesha temple."
                    ]
                },
                {
                    type: "heading",
                    text: "Essential Travel Tips"
                },
                {
                    type: "paragraph",
                    text: "Make sure you plan your journey well. Start early in the morning (around 5:00 AM) to beat the heavy Mumbai outbound traffic. If you are taking the NH 66 route, drive cautiously during night hours as some patches lack street lighting. Having a reliable self-drive car with good ground clearance, like a Maruti Ertiga or Hyundai Creta, will make the ghat sections comfortable and stress-free."
                }
            ]
        },
        {
            slug: "scuba-diving-malvan-tarkarli-guide",
            title: "A Complete Guide to Scuba Diving in Tarkarli & Malvan",
            image: "scuba-diving.webp",
            date: "June 18, 2026",
            author: "Pooja Sawant",
            category: "Adventure",
            readTime: "5 min read",
            summary: "Dive deep into the crystal clear waters of Malvan. Learn about the best dive spots, marine biodiversity, safety measures, and pricing.",
            content: [
                {
                    type: "paragraph",
                    text: "Konkan is not just about sandy beaches and historical forts. Under the blue waves lies a vibrant underwater world waiting to be explored. Tarkarli and Malvan are the premier destinations for scuba diving in Maharashtra, boasting coral reefs, colorful exotic fish, and historical shipwrecks."
                },
                {
                    type: "heading",
                    text: "Best Spots for Scuba Diving"
                },
                {
                    type: "paragraph",
                    text: "There are three primary locations where scuba diving is conducted:"
                },
                {
                    type: "list",
                    items: [
                        "Near Sindhudurg Fort: The shallow water and rocky seabed around the fort are home to beautiful coral patches and schools of reef fish. This is the most popular site for beginners.",
                        "Devbag Sangam: Near the confluence of Karli river and the sea, this area offers deeper waters and a chance to spot larger marine creatures.",
                        "Vengurla Rocks (Dolphin Lighthouse): Ideal for certified divers, featuring deep vertical walls and underwater caves."
                    ]
                },
                {
                    type: "heading",
                    text: "What to Expect Under the Sea"
                },
                {
                    type: "paragraph",
                    text: "During a typical dive of 15 to 25 minutes, you will encounter diverse marine fauna including Parrotfish, Angelfish, Triggerfish, Sea Anemones, Butterflyfish, and if you are lucky, Sea Turtles! Water visibility is best between October and May, ranging from 5 to 12 meters."
                },
                {
                    type: "heading",
                    text: "Tips for Beginners"
                },
                {
                    type: "list",
                    items: [
                        "No swimming skills required: Most tourist scuba dives are 'tandem' or guided, where an instructor controls your movement and breathing apparatus.",
                        "Avoid diving immediately after eating. Keep a gap of at least 2 hours.",
                        "Listen carefully to the hand-signal instructions during the briefing.",
                        "Keep your camera ready: Professional packages include underwater videos and photographs."
                    ]
                }
            ]
        },
        {
            slug: "konkan-monsoon-travel-tips",
            title: "Monsoon Travel in Konkan: Tips, Safety & Places to Visit",
            image: "konkan-monsoon.webp",
            date: "May 10, 2026",
            author: "Vijay Rane",
            category: "Monsoon Special",
            readTime: "4 min read",
            summary: "Experience Konkan at its greenest. Discover the magic of waterfalls, mist-covered ghats, and safety instructions during rains.",
            content: [
                {
                    type: "paragraph",
                    text: "When the monsoon clouds arrive, the Konkan region transforms into a green paradise. The waterfalls come alive, the Sahyadri mountains cover themselves in thick blankets of fog, and the air becomes crisp and clean. Monsoon travel in Konkan is an experience like no other, but it requires preparation."
                },
                {
                    type: "heading",
                    text: "Must-Visit Monsoon Spots in Konkan"
                },
                {
                    type: "list",
                    items: [
                        "Amboli Hill Station: Known as the 'Cherrapunji of Maharashtra', Amboli is famous for its waterfalls, misty viewpoints, and rich reptile biodiversity.",
                        "Savdav Waterfall (Kankavli): A safe, natural waterfall where tourists can play in the water under supervision.",
                        "Amba Ghat: Driving through the fog-laden curves of Amba Ghat during heavy rain is an ethereal experience."
                    ]
                },
                {
                    type: "heading",
                    text: "Safety & Driving Precautions"
                },
                {
                    type: "paragraph",
                    text: "Monsoons can be unpredictable in coastal Maharashtra. Keep these safety tips in mind:"
                },
                {
                    type: "list",
                    items: [
                        "Check weather alerts: Avoid visiting beaches and waterfalls during high tide or heavy red-alert warnings.",
                        "Vehicle health: Ensure your car wipers, headlights, and brakes are in excellent condition. Road surfaces can become extremely slippery.",
                        "Beware of landslides: Mountain passes like Gaganbawda or Radhanagari ghats can experience minor landslides during heavy downpours. Travel during daylight hours whenever possible.",
                        "Pack waterproof gear: Always carry insect repellent, sturdy trekking shoes, and umbrellas/raincoats."
                    ]
                }
            ]
        }
    ];
}
